import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Collapse } from 'react-bootstrap';

import { UseOutletContextAsset } from './Asset';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AssetDA } from '../../../../services/admin/asset.service';
import { Asset } from '../../../../models/asset.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { legendValid, legendInvalid } from '../../../../utils/legend.data.util';

import AppLegend from '../../../../components/element/Legend';
import AppPreloader from '../../../../components/Preloader';

export interface AssetUpdatePageProps {};

let errorResponse: Error;

const AssetUpdatePage: React.FunctionComponent<AssetUpdatePageProps> = props => {
  const {setRoute, companyForUser, categoriesForCompany, servicesForCompany, storageCentersForCompany} = UseOutletContextAsset()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const param = useParams()

  const [mounted, setMounted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [asset, setAsset] = useState<Asset | null>(null)
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [idCategory, setIdCategory] = useState('')
  const [rentalOptions, setRentalOptions] = useState<{ cadence_unit: string; cadence_interval: number; id_service: string }[]>([])

  const [idStorageCenter, setIdStorageCenter] = useState('')
  const [status, setStatus] = useState('')
  const [defaultDepositAmount, setDefaultDepositAmount] = useState('0')
  const [depreciationOpen, setDepreciationOpen] = useState(false)
  const [isDepreciable, setIsDepreciable] = useState(false)
  const [acquisitionValue, setAcquisitionValue] = useState('0')
  const [acquisitionDate, setAcquisitionDate] = useState('')
  const [residualValue, setResidualValue] = useState('0')
  const [depreciationYears, setDepreciationYears] = useState('0')

  const loadAsset = async (id_company: string, id_asset: string) => {
    try {
      const response = await AssetDA.getAsset(id_company, id_asset)
      if (response.status === 200) {
        const data: Asset = response.data
        setAsset(data)
        setName(data.name ?? '')
        setCode(data.code ?? '')
        setDescription(data.description ?? '')
        setIdCategory(data.id_category ?? '')
        setRentalOptions(
          (data.rental_options ?? []).map((o: any) => ({
            cadence_unit: o.cadence_unit,
            cadence_interval: o.cadence_interval ?? 1,
            id_service: typeof o.id_service === 'string' ? o.id_service : (o.id_service?.toString() ?? ''),
          }))
        )
        setIdStorageCenter(data.id_storage_center ?? '')
        setStatus(data.status ?? 'available')
        setDefaultDepositAmount(String(data.default_deposit_amount ?? 0))
        setIsDepreciable(data.is_depreciable ?? false)
        setAcquisitionValue(String(data.acquisition_value ?? 0))
        setAcquisitionDate(data.acquisition_date ?? '')
        setResidualValue(String(data.residual_value ?? 0))
        setDepreciationYears(String(data.depreciation_years ?? 0))
        if (data.is_depreciable) setDepreciationOpen(true)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const addRentalOption = () => setRentalOptions(prev => [...prev, { cadence_unit: 'month', cadence_interval: 1, id_service: '' }])
  const removeRentalOption = (index: number) => setRentalOptions(prev => prev.filter((_, i) => i !== index))
  const updateRentalOption = (index: number, field: string, value: any) =>
    setRentalOptions(prev => prev.map((o, i) => i === index ? { ...o, [field]: value } : o))

  const validateForm = (): boolean => {
    let valid = true

    if (!name.trim()) {
      legendInvalid('legend-asset-name-required')
      valid = false
    } else {
      legendValid('legend-asset-name-required')
    }

    const parsedDeposit = parseFloat(defaultDepositAmount)
    if (defaultDepositAmount && (isNaN(parsedDeposit) || parsedDeposit < 0)) {
      legendInvalid('legend-asset-deposit-valid')
      valid = false
    } else {
      legendValid('legend-asset-deposit-valid')
    }

    if (isDepreciable) {
      const parsedAcq = parseFloat(acquisitionValue)
      if (isNaN(parsedAcq) || parsedAcq < 0) {
        legendInvalid('legend-asset-acquisition-valid')
        valid = false
      } else {
        legendValid('legend-asset-acquisition-valid')
      }
    }

    return valid
  }

  const submitUpdateAsset = async () => {
    if (!companyForUser || !asset || !validateForm()) return

    setLoading(true)

    try {
      const data: any = {
        name: name.trim(),
        code: code.trim(),
        description: description.trim(),
        id_category: idCategory || undefined,
        rental_options: rentalOptions
          .filter(o => o.id_service)
          .map(o => ({ cadence_unit: o.cadence_unit, cadence_interval: o.cadence_interval || 1, id_service: o.id_service })),
        id_storage_center: idStorageCenter || undefined,
        default_deposit_amount: parseFloat(defaultDepositAmount) || 0,
        is_depreciable: isDepreciable,
      }

      if (asset.status !== 'rented') {
        data.status = status
      }

      if (isDepreciable) {
        data.acquisition_value = parseFloat(acquisitionValue) || 0
        data.acquisition_date = acquisitionDate || undefined
        data.residual_value = parseFloat(residualValue) || 0
        data.depreciation_years = parseInt(depreciationYears) || 0
      }

      const response = await AssetDA.putAsset(companyForUser.id, asset.id, data)

      if (response.status === 200) {
        GlobalSwal.fire({
          title: lang.labels.assetUpdatedSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          navigate('/main/inventory/asset/view/' + asset.id, {replace: true})
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (companyForUser && param.id) {
      loadAsset(companyForUser.id, param.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.assets, branch: lang.labels.editAsset}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { asset
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h2 className="fw-bolder">{lang.labels.editAsset}</h2>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="form">
              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.assetName}</label>
                <input className="form-control" type="text" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
                <AppLegend component={undefined} attribute={{check: !!name.trim(), name: "asset-name", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
              </div>
              <div className="row fv-row mb-5">
                <div className="col-xl-6 mb-5 mb-xl-0">
                  <label className="form-label">{lang.labels.assetCode}</label>
                  <input className="form-control" type="text" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
                <div className="col-xl-6">
                  <label className="form-label">{lang.labels.assetDescription}</label>
                  <input className="form-control" type="text" autoComplete="off" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
              <div className="row fv-row mb-5">
                <div className="col-xl-6 mb-5 mb-xl-0">
                  <label className="form-label">{lang.labels.category}</label>
                  <select className="form-select" value={idCategory} onChange={(e) => setIdCategory(e.target.value)}>
                    <option value="">{lang.labels.selectOption}</option>
                    { categoriesForCompany?.map((item, index) => (
                      <option key={index} value={item.id}>{item.display_name ?? item.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-xl-6">
                  <label className="form-label">{lang.labels.storageCenter}</label>
                  <select className="form-select" value={idStorageCenter} onChange={(e) => setIdStorageCenter(e.target.value)}>
                    <option value="">{lang.labels.selectOption}</option>
                    { storageCentersForCompany?.map((item, index) => (
                      <option key={index} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="fv-row mb-5">
                <label className="form-label">{lang.labels.rentalOptions}</label>
                { rentalOptions.map((option, index) => {
                  const svc = servicesForCompany?.find(s => s.id === option.id_service)
                  return (
                    <div key={index} className="d-flex align-items-center gap-2 mb-2">
                      <select className="form-select" style={{maxWidth: '160px'}} value={option.cadence_unit} onChange={e => updateRentalOption(index, 'cadence_unit', e.target.value)}>
                        <option value="day">{lang.labels.cadenceDay}</option>
                        <option value="week">{lang.labels.cadenceWeek}</option>
                        <option value="month">{lang.labels.cadenceMonth}</option>
                        <option value="quarter">{lang.labels.cadenceQuarter}</option>
                        <option value="year">{lang.labels.cadenceYear}</option>
                      </select>
                      <select className="form-select flex-grow-1" value={option.id_service} onChange={e => updateRentalOption(index, 'id_service', e.target.value)}>
                        <option value="">{lang.labels.selectService}</option>
                        { servicesForCompany?.map(s => (
                          <option key={s.id} value={s.id}>{s.display_name ?? s.name}</option>
                        ))}
                      </select>
                      <span className="text-muted text-nowrap" style={{fontVariantNumeric: 'tabular-nums', minWidth: '80px'}}>
                        {svc?.price_sale?.amount != null ? `${svc.price_sale.amount.toFixed(2)} ${companyForUser?.currency?.code ?? 'CHF'}` : '—'}
                      </span>
                      <button type="button" className="btn btn-sm btn-icon btn-light-danger flex-shrink-0" onClick={() => removeRentalOption(index)}>
                        <i className="bi bi-x-lg fs-8"></i>
                      </button>
                    </div>
                  )
                })}
                <button type="button" className="btn btn-sm btn-light-primary mt-1" onClick={addRentalOption}>
                  <i className="bi bi-plus-lg me-1"></i>{lang.labels.addRentalOption}
                </button>
              </div>
              <div className="fv-row mb-5">
                <label className="form-label">{lang.labels.status}</label>
                { asset.status === 'rented'
                  ?
                  <div>
                    <span className="badge badge-light-warning">{lang.labels.assetStatusRented}</span>
                    <p className="form-text text-muted fs-8 mt-1">{lang.labels.assetStatusManagedByRental}</p>
                  </div>
                  :
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="available">{lang.labels.assetStatusAvailable}</option>
                    <option value="out_of_service">{lang.labels.assetStatusOutOfService}</option>
                  </select>
                }
              </div>
              <div className="fv-row mb-5">
                <label className="form-label">{lang.labels.defaultDepositAmount}</label>
                <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={defaultDepositAmount} onChange={(e) => setDefaultDepositAmount(e.target.value)} style={{fontVariantNumeric: 'tabular-nums'}} />
                <AppLegend component={undefined} attribute={{check: !defaultDepositAmount || (!isNaN(parseFloat(defaultDepositAmount)) && parseFloat(defaultDepositAmount) >= 0), name: "asset-deposit", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}} />
              </div>
              <div className="separator separator-dashed my-5"></div>
              <div className="d-flex align-items-center cursor-pointer mb-3" onClick={() => setDepreciationOpen(!depreciationOpen)}>
                <span className="svg-icon svg-icon-3 me-2">
                  <i className={`bi ${depreciationOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                </span>
                <h4 className="fw-bolder m-0">{lang.labels.depreciationSection}</h4>
              </div>
              <Collapse in={depreciationOpen}>
                <div>
                  <div className="fv-row mb-5">
                    <div className="d-flex flex-stack">
                      <div className="d-flex flex-column">
                        <label className="form-label mb-0">{lang.labels.isDepreciable}</label>
                      </div>
                      <div className="d-flex justify-content-end">
                        <div className="form-check form-check-solid form-check-custom form-switch">
                          <input className="form-check-input w-50px h-30px" type="checkbox" checked={isDepreciable} onChange={(e) => setIsDepreciable(e.target.checked)} />
                        </div>
                      </div>
                    </div>
                  </div>
                  { isDepreciable &&
                    <>
                      <div className="row fv-row mb-5">
                        <div className="col-xl-6 mb-5 mb-xl-0">
                          <label className="form-label">{lang.labels.acquisitionValue}</label>
                          <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={acquisitionValue} onChange={(e) => setAcquisitionValue(e.target.value)} style={{fontVariantNumeric: 'tabular-nums'}} />
                          <AppLegend component={undefined} attribute={{check: !isNaN(parseFloat(acquisitionValue)) && parseFloat(acquisitionValue) >= 0, name: "asset-acquisition", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}} />
                        </div>
                        <div className="col-xl-6">
                          <label className="form-label">{lang.labels.acquisitionDate}</label>
                          <input className="form-control" type="date" value={acquisitionDate} onChange={(e) => setAcquisitionDate(e.target.value)} />
                        </div>
                      </div>
                      <div className="row fv-row mb-5">
                        <div className="col-xl-6 mb-5 mb-xl-0">
                          <label className="form-label">{lang.labels.residualValue}</label>
                          <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={residualValue} onChange={(e) => setResidualValue(e.target.value)} style={{fontVariantNumeric: 'tabular-nums'}} />
                        </div>
                        <div className="col-xl-6">
                          <label className="form-label">{lang.labels.depreciationYears}</label>
                          <input className="form-control" type="number" step="1" min="0" placeholder="0" value={depreciationYears} onChange={(e) => setDepreciationYears(e.target.value)} style={{fontVariantNumeric: 'tabular-nums'}} />
                        </div>
                      </div>
                    </>
                  }
                </div>
              </Collapse>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-light me-3" type="button" onClick={() => navigate('/main/inventory/asset/view/' + asset.id)}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" disabled={loading} onClick={submitUpdateAsset}>
              {loading ? lang.labels.pleaseWait : lang.labels.editAsset}
            </button>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default AssetUpdatePage;
